import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import StarRating from './StarRating';
import './ReviewInvitePopup.css';

// Popup ชวนรีวิวดาวระบบ (เด้งซ้ำทุกเดือนจนกว่าจะรีวิว)
// - เด้งเฉพาะผู้ที่ยังไม่เคยรีวิว และยืนยันอีเมลแล้ว (คนยังไม่ยืนยันอีเมลรีวิวไม่ได้ จึงไม่กวน)
// - บัญชีเปิดใหม่ยังไม่เด้ง — ให้ใช้งานระบบไปก่อนอย่างน้อย MIN_ACCOUNT_AGE_DAYS วัน ค่อยชวนรีวิว
// - รีวิวได้ในตัว popup เลย: ดาว + ชื่อที่แสดง (ไม่ระบุตัวตนก็ได้) + คอมเมนต์ (ไม่บังคับ)
// - กดปิด / ไว้ภายหลัง -> เงียบถึงสิ้นเดือนนี้ แล้วเด้งใหม่ตั้งแต่วันที่ 1 ของเดือนถัดไป
// - รีวิวแล้ว -> ไม่เด้งอีกเลย (แก้ไขรีวิวได้ที่เมนูหน้า /reviews)
const MONTH_KEY = 'imoney_review_invite_closed_month';
const SHOW_DELAY_MS = 3200; // หน่วงให้ popup PWA / สหกรณ์ (ถ้ามี) แสดงก่อน ไม่ให้ซ้อนกัน
const MIN_ACCOUNT_AGE_DAYS = 14; // เปิดบัญชีมาแล้วอย่างน้อยกี่วันจึงเริ่มชวนรีวิว

// บัญชียังใหม่เกินไปไหม — ไม่รู้วันสร้าง (ข้อมูลเก่า) ให้ถือว่าไม่ใหม่ ปล่อยตาม logic เดิม
function accountTooNew(user) {
    if (!user?.created_at) return false;
    const created = new Date(user.created_at).getTime();
    if (isNaN(created)) return false;
    return Date.now() - created < MIN_ACCOUNT_AGE_DAYS * 24 * 60 * 60 * 1000;
}

// 'YYYY-MM' ของเดือนปัจจุบัน — พอขึ้นเดือนใหม่ค่าจะไม่ตรงกับที่เก็บไว้ popup จึงกลับมาเด้งเอง
function currentMonthKey() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function closedThisMonth() {
    try { return localStorage.getItem(MONTH_KEY) === currentMonthKey(); } catch (e) { return false; }
}
function setClosedThisMonth() {
    try { localStorage.setItem(MONTH_KEY, currentMonthKey()); } catch (e) {}
}

export default function ReviewInvitePopup() {
    const { user } = useAuth();
    const [visible, setVisible] = useState(false);
    const [rating, setRating] = useState(0);
    const [displayName, setDisplayName] = useState('');
    const [comment, setComment] = useState('');
    const [saving, setSaving] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (closedThisMonth()) return;
        if (!user?.email_verified) return; // ยังยืนยันอีเมลไม่ได้รีวิว — ไม่ต้องเด้ง
        if (accountTooNew(user)) return; // บัญชีเปิดใหม่ ยังไม่คุ้นระบบ — อย่าเพิ่งชวนรีวิว
        let cancelled = false;
        let timer = null;
        (async () => {
            try {
                const r = await api.get('/reviews/me');
                if (cancelled || r.data.review) return; // เคยรีวิวแล้ว ไม่เด้ง
                timer = setTimeout(() => {
                    if (cancelled || closedThisMonth()) return;
                    // ถ้ามี popup อื่นแสดงอยู่ (สหกรณ์/PWA) ข้ามรอบนี้ไปก่อน กันซ้อนกัน
                    if (document.querySelector('.coop-pop-backdrop, .pwa-pop-backdrop')) return;
                    setVisible(true);
                }, SHOW_DELAY_MS);
            } catch (e) {
                // เงียบไว้ — ไม่ให้ popup กวนถ้า API มีปัญหา
            }
        })();
        return () => { cancelled = true; if (timer) clearTimeout(timer); };
    }, [user]);

    // ปิดชั่วคราว — เดือนหน้าวันที่ 1 ค่อยเตือนใหม่ (จนกว่าจะรีวิว)
    const closeForThisMonth = () => {
        setClosedThisMonth();
        setVisible(false);
    };

    const submit = async () => {
        if (!rating || saving) return;
        setSaving(true);
        setError('');
        try {
            await api.post('/reviews', { rating, comment, display_name: displayName });
            setDone(true);
            setClosedThisMonth();
            setTimeout(() => setVisible(false), 1800);
        } catch (e) {
            setError(e.response?.data?.error || 'บันทึกไม่สำเร็จ กรุณาลองใหม่');
        } finally {
            setSaving(false);
        }
    };

    if (!visible) return null;

    return (
        <div className="review-pop-backdrop" role="dialog" aria-modal="true" aria-labelledby="review-pop-title">
            <div className="review-pop" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="review-pop-x" onClick={closeForThisMonth} aria-label="ปิด">×</button>

                {done ? (
                    <div className="review-pop-thanks">
                        <div className="review-pop-thanks-icon" aria-hidden="true">💙</div>
                        <h3 className="review-pop-title">ขอบคุณสำหรับรีวิว!</h3>
                        <p className="review-pop-lead">ความเห็นของคุณช่วยให้เราพัฒนาระบบให้ดียิ่งขึ้น</p>
                    </div>
                ) : (
                    <>
                        <div className="review-pop-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                            </svg>
                        </div>

                        <h3 id="review-pop-title" className="review-pop-title">ให้คะแนนระบบหน่อยนะ</h3>
                        <p className="review-pop-lead">
                            ใช้งานระบบเป็นอย่างไรบ้าง? ให้ดาวและบอกความเห็นได้เลย
                            จะระบุตัวตนหรือไม่ก็ได้
                        </p>

                        <div className="review-pop-stars">
                            <StarRating value={rating} onChange={(n) => { setRating(n); setError(''); }} size={40} />
                        </div>

                        <input
                            className="review-pop-input"
                            type="text"
                            placeholder="ชื่อที่จะแสดง (ไม่ระบุ = ผู้ใช้ระบบ)"
                            maxLength={100}
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                        />
                        <textarea
                            className="review-pop-textarea"
                            placeholder="ความคิดเห็นเพิ่มเติม (ไม่บังคับ)"
                            maxLength={1000}
                            rows={3}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                        />

                        {error && <div className="review-pop-error">{error}</div>}

                        <button type="button" className="review-pop-primary" onClick={submit} disabled={!rating || saving}>
                            {saving ? 'กำลังบันทึก...' : 'ส่งรีวิว'}
                        </button>

                        <button type="button" className="review-pop-later" onClick={closeForThisMonth}>
                            ไว้ภายหลัง (เตือนอีกครั้งต้นเดือนหน้า)
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
